import type { CollectionColorEnum } from '@assembly-web/services';
import { mapHexCodeToEmoticon } from '@assembly-web/services';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { Checkbox } from '../../../DesignSystem/Inputs/Checkbox';
import { IconButton } from '../../../DesignSystem/Inputs/IconButton';
import { classNames } from '../../../DesignSystem/Utils/classNames';
import { mapColorToCssClass } from '../../Shared/EmojiColorPicker/utils';
import type { SelectableOptionProps } from '../SelectableList';
import { SelectableList } from '../SelectableList';

export type MenuItem = 'Flows' | 'Collections' | 'Apps';

export type BaseInFilterOption = { id: string; value: string };

export type FlowFilterOption = BaseInFilterOption & { emoticon: string };

export type CollectionsOption = BaseInFilterOption & {
  emoticon: string;
  collectionColor: CollectionColorEnum;
};

export type AppFilterOption = BaseInFilterOption & { imgUrl: string };

export type InFilterSelectableListProps = {
  isLoading: boolean;
  onLoadMore: () => void;
  isFetchingNextPage?: boolean;
  onSearchChange: (value: string) => void;
  onMenuSelected: (menuItem: MenuItem | '') => void;
  flowsOptions: {
    options: FlowFilterOption[];
    selectedOptions: FlowFilterOption[];
  };
  collectionsOptions: {
    options: CollectionsOption[];
    selectedOptions: CollectionsOption[];
  };
  appsOptions: {
    options: AppFilterOption[];
    selectedOptions: AppFilterOption[];
  };
  onDoneClick: (currentSelectedOptions: SelectableOptionProps[]) => void;
  hasNextPage?: boolean;
  onClearAllClick: () => void;
};

export function InFilterSelectableList({
  isLoading,
  onLoadMore,
  onDoneClick,
  onClearAllClick,
  onSearchChange,
  onMenuSelected,
  appsOptions,
  isFetchingNextPage,
  flowsOptions,
  collectionsOptions,
  hasNextPage,
}: InFilterSelectableListProps) {
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('');

  const getCurrentSelectedListOptionItems = () => {
    if (selectedMenuItem === 'Flows') {
      return {
        options: flowsOptions.options,
        selectedOptions: flowsOptions.selectedOptions,
      };
    } else if (selectedMenuItem === 'Collections') {
      return {
        options: collectionsOptions.options,
        selectedOptions: collectionsOptions.selectedOptions,
      };
    } else {
      return {
        options: appsOptions.options,
        selectedOptions: appsOptions.selectedOptions,
      };
    }
  };

  const handleMenuSelected = (selectedList: MenuItem | '') => {
    onMenuSelected(selectedList);
    setSelectedMenuItem(selectedList);
  };

  const primaryOptions = [
    {
      id: '1',
      value: 'Flows',
      selectedOptionsLength: flowsOptions.selectedOptions.length,
    },
    {
      id: '2',
      value: 'Collections',
      selectedOptionsLength: collectionsOptions.selectedOptions.length,
    },
    {
      id: '3',
      value: 'Apps',
      selectedOptionsLength: appsOptions.selectedOptions.length,
    },
  ];

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedOptionIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : prevIndex
      );
    } else if (e.key === 'ArrowDown' || e.key === 'Tab') {
      e.preventDefault();
      setFocusedOptionIndex((prevIndex) =>
        prevIndex < primaryOptions.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (e.key === 'Enter' || e.code === 'Space') {
      e.preventDefault();
      handleMenuSelected(primaryOptions[focusedOptionIndex].value as MenuItem);
    }
  };

  const Option = ({ option }: { option: SelectableOptionProps }) => {
    return (
      <div className="flex items-center gap-2">
        {'emoticon' in option && (
          <div className="px-[1px]">
            {mapHexCodeToEmoticon(option.emoticon as 'string')}
          </div>
        )}
        {'imgUrl' in option && (
          <img
            src={option.imgUrl as string}
            alt={option.id}
            className="h-4 w-4"
          />
        )}
        <TextStyle
          variant="sm-regular"
          className={twMerge(
            'line-clamp-1 text-gray-8',
            selectedMenuItem === 'Collections' && 'rounded-lg px-2',
            'collectionColor' in option &&
              mapColorToCssClass(option.collectionColor as CollectionColorEnum)
          )}
        >
          {option.value}
        </TextStyle>
      </div>
    );
  };

  return (
    <section>
      <div
        className={twMerge(
          'box-border flex w-[280px] flex-col gap-2 rounded-lg outline-none',
          !selectedMenuItem && 'py-1'
        )}
      >
        <div onKeyDown={handleKeyDown} tabIndex={-1} role="menu">
          {!selectedMenuItem &&
            primaryOptions.map((option, index) => (
              <div
                key={index}
                className={classNames(
                  'group flex cursor-pointer items-center justify-between py-2 pl-4 pr-3 outline-none hover:bg-gray-2 focus:bg-gray-2',
                  { 'rounded-md bg-gray-4': index === focusedOptionIndex }
                )}
                tabIndex={0}
                role="menuitem"
                aria-label={option.value}
                onKeyDown={() => {}}
                onClick={() => handleMenuSelected(option.value as MenuItem)}
              >
                <TextStyle variant="sm-regular" className="text-gray-8">
                  {option.value}
                  {option.selectedOptionsLength > 0 && (
                    <span> • {option.selectedOptionsLength} selected</span>
                  )}
                </TextStyle>
                <div>
                  <IconButton
                    size="xSmall"
                    onClick={() => {
                      handleMenuSelected(option.value as MenuItem);
                    }}
                    variation="tertiaryLite"
                    tabIndex={-1}
                    className="group-hover:bg-gray-2"
                  >
                    <ChevronRightIcon className="h-4 w-4 " aria-hidden="true" />
                  </IconButton>
                </div>
              </div>
            ))}
        </div>

        {selectedMenuItem && (
          <SelectableList
            showSearch={selectedMenuItem !== 'Apps'}
            placeholder="Search"
            isLoading={isLoading}
            onLoadMore={onLoadMore}
            showNestedDropdownHeader
            onSearchChange={onSearchChange}
            nestedDropdownHeaderText={selectedMenuItem}
            options={getCurrentSelectedListOptionItems().options}
            selectedOptions={
              getCurrentSelectedListOptionItems().selectedOptions
            }
            onBackButtonClick={() => {
              handleMenuSelected('');
            }}
            onDoneClick={(currentSelectedOptions) => {
              handleMenuSelected('');
              onDoneClick(currentSelectedOptions);
            }}
            onClearAllClick={() => {
              onClearAllClick();
              handleMenuSelected('');
            }}
            renderOption={(option) => <Option option={option} />}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            loader={
              <div
                className="absolute flex animate-pulse items-center gap-2 px-2 py-2"
                role="menuitem"
              >
                <Checkbox
                  disabled
                  name="selectable-option-loader"
                  aria-label="loader"
                />
                <div className="h-[16px] w-[132px] rounded-full bg-gray-5"></div>
              </div>
            }
            className="pt-0"
          />
        )}
      </div>
    </section>
  );
}
